import { DetailedRoute } from '@/generated/model/detailedRoute';
import { RefObject, useCallback, useRef } from 'react';

function isScrollable(el: HTMLElement) {
  const style = window.getComputedStyle(el);
  return /(auto|scroll)/.test(style.overflowY) && el.scrollHeight > el.clientHeight;
}

function getScrollContainer(el: HTMLElement, preferredContainer: HTMLElement | null) {
  if (preferredContainer?.contains(el) && isScrollable(preferredContainer)) {
    return preferredContainer;
  }

  let parent = el.parentElement;
  while (parent && parent !== document.body) {
    if (isScrollable(parent)) return parent;
    parent = parent.parentElement;
  }

  return null;
}

export function scrollToElement({
  element,
  preferredContainer,
  offset = 0,
  behavior = 'smooth',
}: {
  element: HTMLElement | null | undefined;
  preferredContainer: HTMLElement | null;
  offset?: number;
  behavior?: ScrollBehavior;
}) {
  if (!element) return;

  const container = getScrollContainer(element, preferredContainer);
  if (!container) return;

  const top =
    element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - offset;

  container.scrollTo({ top: Math.max(top, 0), behavior });
}

export function useRouteListScroll(containerRef: RefObject<HTMLElement | null>) {
  const refs = useRef(new Map<number, HTMLElement>());

  const registerRef = useCallback(
    (route: DetailedRoute | undefined) => (el: HTMLElement | null) => {
      if (!route) return;
      if (el) refs.current.set(route.id, el);
      else refs.current.delete(route.id);
    },
    []
  );

  const scrollTo = useCallback(
    (id: number) => {
      scrollToElement({ element: refs.current.get(id), preferredContainer: containerRef.current });
    },
    [containerRef]
  );

  return { registerRef, scrollTo };
}
